import React, { useState } from 'react';
import { bulkAutoCompleteOrders } from '../../services/autoCompleteService.js';
import { getCurrentUser } from '../../utils/auth';
import { toast } from 'react-toastify';
import './BulkWarehouseModal.css';

const BulkAutoCompleteModal = ({ isOpen, onClose, onComplete }) => {
  const [orderNosInput, setOrderNosInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const parseOrderNumbers = () => {
    if (!orderNosInput.trim()) return [];
    return orderNosInput
      .split(/[\n,]/)
      .map(n => parseInt(n.trim()))
      .filter(n => !isNaN(n));
  };

  const orderCount = parseOrderNumbers().length;

  const handleExecute = async () => {
    const orders = parseOrderNumbers();

    if (orders.length === 0) {
      toast.warning('Please enter order numbers to auto-complete');
      return;
    }

    const currentUser = getCurrentUser();
    if (!currentUser) {
      toast.error('You must be logged in to perform this action');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await bulkAutoCompleteOrders(orders, currentUser);

      if (response.success || response.completed > 0) {
        setResult(response);

        toast.success(
          <div>
            <strong>Bulk Auto-Complete Done!</strong>
            <br />
            {response.completed} of {response.totalOrders} orders completed
            {response.failed > 0 && <><br />{response.failed} failed</>}
          </div>,
          { autoClose: 5000 }
        );

        // Notify parent to refresh data
        if (onComplete) {
          onComplete();
        }
      } else {
        toast.error(response.error || 'Bulk auto-complete failed');
        setResult(response);
      }
    } catch (err) {
      console.error('Execute error:', err);
      toast.error('Failed to execute bulk auto-complete: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setOrderNosInput('');
    onClose();
  };

  return (
    <div className="bulk-modal-overlay" onClick={handleClose}>
      <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>

        <div className="bulk-modal-header">
          <h2>Bulk Auto-Complete Orders</h2>
          <button className="close-button" onClick={handleClose}>x</button>
        </div>

        <div className="bulk-modal-content">

          <div className="bulk-form-group">
            <label>
              Order Numbers to Auto-Complete (one per line)
              <span className="inclusion-count">
                {orderCount > 0 && ` - ${orderCount} orders`}
              </span>
            </label>
            <textarea
              value={orderNosInput}
              onChange={(e) => {
                setOrderNosInput(e.target.value);
                setResult(null);
              }}
              placeholder="Enter order numbers (one per line)&#10;Example:&#10;45001&#10;45002&#10;45003"
              rows={12}
              disabled={loading}
            />
            <small className="help-text">
              Each order will be auto-completed: tasks marked complete, order set to "Completed",
              and warehouse updated based on your location.
              Note: Inventory status (InvStatus) will NOT be changed.
            </small>
          </div>

          {result && (
            <div className="bulk-preview">
              <h3>Results</h3>
              <div className="preview-stats">
                <div className="preview-stat">
                  <span className="stat-label">Total Orders:</span>
                  <span className="stat-value">{result.totalOrders}</span>
                </div>
                <div className="preview-stat">
                  <span className="stat-label">Completed:</span>
                  <span className="stat-value highlight">{result.completed}</span>
                </div>
                <div className="preview-stat">
                  <span className="stat-label">Failed:</span>
                  <span className="stat-value" style={{ color: result.failed > 0 ? '#ef4444' : 'inherit' }}>
                    {result.failed}
                  </span>
                </div>
                <div className="preview-stat">
                  <span className="stat-label">Skipped:</span>
                  <span className="stat-value">{result.skipped || 0}</span>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <strong style={{ color: '#ef4444' }}>Errors:</strong>
                  <ul style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    margin: '8px 0',
                    paddingLeft: '20px',
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)'
                  }}>
                    {result.errors.slice(0, 20).map((err, idx) => (
                      <li key={idx}>Order {err.orderNo}: {err.error}</li>
                    ))}
                    {result.errors.length > 20 && (
                      <li>...and {result.errors.length - 20} more errors</li>
                    )}
                  </ul>
                </div>
              )}

              <p className="preview-message">
                {result.completed > 0
                  ? `Successfully auto-completed ${result.completed} orders`
                  : 'No orders were completed'}
              </p>
            </div>
          )}

        </div>

        <div className="bulk-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {result ? 'Close' : 'Cancel'}
          </button>

          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleExecute}
              disabled={loading || orderCount === 0}
            >
              {loading ? 'Processing...' : `Auto-Complete ${orderCount} Orders`}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

// Keep the old export name for backwards compatibility
export default BulkAutoCompleteModal;
